import React from 'react';
import { initializeTracing } from 'Helpers/tracing';
import { Page500V3 } from 'Pages/5XX';
import { logError } from 'Helpers/errors';
import { useGetAppConfig } from './graphql/getAppConfig.generated';
import { useUpdateAppConfig } from './AppConfigContext';

// Do not prefetch the ChatBot component, as it is only used by SaaS customers
// to save some bandwidth
const ChatBot = React.lazy(() => import('Components/utils/ChatBot'));

// Defining the didInit variable here might seem like a bad practice, since it's a global variable
// and it's not used inside react's state management. However, it's used to prevent prop drilling
// and this method is even recommended by the React team for initialization purposes.
// see https://react.dev/learn/you-might-not-need-an-effect#initializing-the-application
// Since we are only initializing the application once, after a successful login,
// We want this to be a global variable so that we can prevent the initialization from happening
// multiple times, when users log out and log back in again.
let didInit = false;

const AppBootstrapper = ({
  fallback,
  children,
}: {
  fallback: React.ReactNode;
  children: React.ReactNode;
}) => {
  const [isInitialized, setIsInitialized] = React.useState(didInit);
  const { data, loading, error } = useGetAppConfig();
  const updateAppConfig = useUpdateAppConfig();

  React.useEffect(() => {
    if (!didInit && !loading && data && !error) {
      initializeTracing(data.appConfig);
      updateAppConfig(data.appConfig);
      didInit = true;
      setIsInitialized(true);
    }
  }, [data, loading, error, updateAppConfig]);

  if (error) {
    logError(error);
    return <Page500V3 />;
  }

  if (!isInitialized) {
    return fallback;
  }

  // Self-hosted customers do not have pylonAppId values
  // configured. Therefore, we can check if pylonAppId exists
  // to determine if chat bot should be loaded
  return (
    <>
      {data.appConfig.pylonAppId && <ChatBot />}
      {children}
    </>
  );
};

export default React.memo(AppBootstrapper);
